import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { patientFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type PatientAcceptedTelemonitoringCardProps = {
  patientId: string;
  patientFirstName: string;
  patientLastName: string;
  onStartTelemonitoring: () => void;
  className?: string;
};

export const PatientAcceptedTelemonitoringCard: React.FC<PatientAcceptedTelemonitoringCardProps> = ({
  patientId,
  patientFirstName,
  patientLastName,
  onStartTelemonitoring,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <ColoredCard
      color="purple"
      standout
      id={`patient-accepted-telemonitoring-card-${patientId}`}
      className={cx(styles.container, className)}
    >
      <CardHeader
        title={
          <Typography variant="body" className={styles.headerText}>
            {patientFormattedName(patientLastName, patientFirstName)}
          </Typography>
        }
      />
      <CardContent className={styles.tagsContainer}>
        <Tag color="yellow" label={t("inclusionAgent.lastStep")} />
        <Tag color="green" label={t("inclusionAgent.patientAccepted")} icon={<Icons.check className={styles.icon} />} />
      </CardContent>
      <CardFooter className={styles.actions}>
        <Button
          data-action="inclusion.telemonitoring.start"
          variant="contained"
          onClick={() => onStartTelemonitoring()}
        >
          {t("inclusionAgent.startTelemonitoring")}
        </Button>
      </CardFooter>
    </ColoredCard>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
  }),
  tagsContainer: css({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    overflow: "clip",
  }),
  headerText: css({
    fontWeight: "bold",
  }),
  actions: css({
    justifyContent: "flex-start",
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
